import { useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { RenderingProvider, useContent } from '@eneco/cms';
import { type Link, linkWithUrl } from '@eneco/cms-types';
import {
  BackButton,
  Dialog,
  NavigationMenu,
  NavigationMenuWrapper,
  StyledDialogContentMobile,
  StyledNavigationMenuContentDesktop,
  StyledNavigationMenuTriger,
  StyledTrigger,
} from '@eneco/custom-components/MegaMenu';
import { Link as NextLink, useRouter } from '@eneco/next';
import { Box, Divider, Grid, NavLink, PageGrid, Stack, Stretch, Text, VisuallyHidden } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';
import { useI18nTranslation } from '@eneco/sparky/providers';

import type { ContentCardContent } from './content-card-content.types';
import ContentCard from './ContentCard';
import { getIllustration } from './illustrations';
import type { MegaMenuV2Content, MegaMenuV2Item } from './mega-menu-v2-content.types';

/**
 * `MegaMenuV2` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component and its division (`libs/sitecore/components/content/src/MegaMenuV2/
 * MegaMenuV2.tsx`, `.../MegaMenuV2Division/MegaMenuV2Division.tsx`): a Radix `NavigationMenu` with
 * hover-opened dropdowns on desktop, a `Dialog` per division on mobile. `lib:cms-components` was widened
 * to allow `lib:custom-components` for this — see `eslint.config.mjs`.
 *
 * The two lanes hand the menu over in different shapes, normalized into one by `toDivisions`:
 * - Sitecore: the divisions on the block itself, each with a `title`, a `link` and its own `items`
 *   (`TeaserLink`s and `ContentCard`s, told apart by `templateName`).
 * - Sanity: the `siteWideContent` `navigationMenu` document's `items`, a `navItem` (one link) or a
 *   `navGroup` (a heading over its own `navItem`s, whose `description` is the teaser text). The
 *   migration turned a division's `ContentCard` into a plain `navItem`, so Sanity never renders a card.
 *
 * The document's own `title` is deliberately not rendered — it is the editor's label for the menu
 * ("Mega menu"), and the legacy component never shows it either.
 *
 * Links go through `@eneco/next`'s `Link`, the component legacy gets from `useLinkComponent` (`lib:link` is
 * not allowed here): client-side navigation within the container, the `?_cms=` override kept, and the
 * current page marked on teaser links.
 */
const MegaMenuV2: FC = () => {
  const content = useContent<MegaMenuV2Content>();
  const mainNavigation = useI18nTranslation('mainNavigation');
  const isDesktop = useMediaQuery('lg');

  const divisions = toDivisions(content.items ?? content.siteWideContent?.items);
  if (divisions.length === 0) return null;

  return isDesktop ? (
    <NavigationMenuWrapper>
      <NavigationMenu.Root aria-label={mainNavigation}>
        <NavigationMenu.List asChild>
          <Stack as="ul" direction="row" gap="6">
            {divisions.map(division => (
              <NavigationMenu.Item key={division.key}>
                <DesktopDivision division={division} />
              </NavigationMenu.Item>
            ))}
          </Stack>
        </NavigationMenu.List>
      </NavigationMenu.Root>
    </NavigationMenuWrapper>
  ) : (
    <Stack gap="7" direction={{ initial: 'column', md: 'row' }}>
      <Stack.Item as="nav" grow aria-label={mainNavigation}>
        <Grid as="ul" gridTemplateColumns={{ md: '1fr' }} gap="5">
          {divisions.map(division => (
            <li key={division.key}>
              <MobileDivision division={division} />
            </li>
          ))}
        </Grid>
      </Stack.Item>
    </Stack>
  );
};

const DesktopDivision: FC<{ division: Division }> = ({ division: { title, link, itemCount, links, cards } }) => {
  if (itemCount === 0) {
    return (
      <Stretch>
        <Stack alignY="center">
          <MenuLink link={link}>
            <Text size="BodyL">{title}</Text>
          </MenuLink>
        </Stack>
      </Stretch>
    );
  }

  const isNarrow = cards.length > 0 || itemCount < 5;

  return (
    <>
      <StyledNavigationMenuTriger>
        {title}
        <ChevronDownIcon color="iconOnBackgroundVarFive" />
      </StyledNavigationMenuTriger>
      <Stack>
        <StyledNavigationMenuContentDesktop>
          <Box paddingY="16" backgroundColor="backgroundPrimary">
            <PageGrid>
              <PageGrid.Item gridColumn="1/-1">
                <Grid gridTemplateColumns={isNarrow ? '2fr 1fr' : 'auto'}>
                  <Stack.Item>
                    <Grid rowGap="10" columnGap="6" gridTemplateColumns={isNarrow ? '1fr 1fr' : '1fr 1fr 1fr'} as="ul">
                      {links.map(teaserLink => (
                        <Stack key={teaserLink.key} as="li" direction="row" alignY="center" gap="3">
                          <TeaserLinkContent teaserLink={teaserLink} />
                        </Stack>
                      ))}
                    </Grid>
                  </Stack.Item>
                  {cards.length > 0 ? (
                    <Grid.Item gridColumn="2" gridRow="1/-1">
                      {cards.map(card => (
                        <MenuContentCard key={card.key} content={card.content} />
                      ))}
                    </Grid.Item>
                  ) : null}
                </Grid>
              </PageGrid.Item>
            </PageGrid>
          </Box>
        </StyledNavigationMenuContentDesktop>
      </Stack>
    </>
  );
};

const MobileDivision: FC<{ division: Division }> = ({ division: { title, link, itemCount, links, cards } }) => {
  const [isOpen, setOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = () => setOpen(false);

    router.events.on('beforeHistoryChange', handleRouteChange);
    return () => router.events.off('beforeHistoryChange', handleRouteChange);
  }, [router]);

  const toggle = () => setOpen(prevState => !prevState);

  if (itemCount === 0) {
    return (
      <Stack gap="7">
        <MenuLink link={link}>
          <Text size="BodyL">{title}</Text>
        </MenuLink>
        <Divider />
      </Stack>
    );
  }

  return (
    <Stack alignX="justify" gap="7" alignY="center">
      <Dialog.Root modal={false} open={isOpen}>
        <StyledTrigger onClick={toggle}>
          {title}
          <ChevronRightIcon />
        </StyledTrigger>
        <Divider />
        <StyledDialogContentMobile>
          <Stretch>
            <Box overflow="auto" paddingY="5" paddingX={{ initial: '5', sm: '10' }} backgroundColor="backgroundPrimary">
              <Stack gap="6">
                {/*
                  Radix requires a `Dialog.Title` for screen-reader users and warns without one. The back
                  button already shows the title, so the accessible one is hidden rather than duplicated.
                  The legacy division omits it entirely.
                */}
                <Dialog.Title asChild>
                  <VisuallyHidden>{title}</VisuallyHidden>
                </Dialog.Title>
                <BackButton onClick={toggle}>
                  <ChevronLeftIcon color="currentColor" />
                  {title}
                </BackButton>
                <Divider />
                <Stack gap="6" direction={{ initial: 'column', md: 'row' }}>
                  <Stack.Item grow>
                    <Grid as="ul" rowGap="10" columnGap="6" gridTemplateColumns={{ initial: '1fr', md: '1fr 1fr' }}>
                      {links.map(teaserLink => (
                        <Grid.Item key={teaserLink.key} as="li">
                          <Stack direction="row" alignY="center" gap="3">
                            <TeaserLinkContent teaserLink={teaserLink} isTeaserHidden />
                          </Stack>
                        </Grid.Item>
                      ))}
                    </Grid>
                  </Stack.Item>
                  {cards.length > 0 && (
                    <Stack.Item grow>
                      {cards.map(card => (
                        <MenuContentCard key={card.key} content={card.content} />
                      ))}
                    </Stack.Item>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Stretch>
        </StyledDialogContentMobile>
      </Dialog.Root>
    </Stack>
  );
};

/**
 * One teaser link, with its illustration when it has one.
 *
 * The href is the last-resort label. It is reached only when the link has neither its own `text` nor a
 * target page whose `browserTitle` could stand in (`mappers.ts#referenceLabel`), which for GB content
 * means an internal link the migration left unresolved.
 */
const TeaserLinkContent: FC<{ teaserLink: TeaserLink; isTeaserHidden?: boolean }> = ({
  teaserLink: { link, illustration, teaserText },
  isTeaserHidden,
}) => {
  const Illustration = getIllustration(illustration);

  return (
    <>
      {Illustration && <Illustration size="small" color="iconSecondary" />}
      <Stack>
        <MenuLink link={link} currentMatch="exact">
          <Text size="BodyL">{link.text ?? link.href}</Text>
        </MenuLink>
        <Text color="textLowEmphasis" weight="regular" size="BodyS" aria-hidden={isTeaserHidden || undefined}>
          {teaserText}
        </Text>
      </Stack>
    </>
  );
};

/**
 * A `NavLink` behind `@eneco/next`'s `Link`, as legacy wraps each menu link. Legacy passes only `href` and
 * `linktype`, so a link's `target` is not applied there either.
 */
const MenuLink: FC<PropsWithChildren<{ link?: Link; currentMatch?: 'exact' }>> = ({ link, currentMatch, children }) => (
  <NextLink href={link?.href ?? ''} linkType={link?.linkType ?? ''} currentMatch={currentMatch}>
    <NavLink>{children}</NavLink>
  </NextLink>
);

/**
 * A division's `ContentCard`, rendered by the registered `ContentCard` with the card item as its content,
 * elevated as the legacy division asks (`setElevation: '1'` there, `'true'` in this lane's port).
 */
const MenuContentCard: FC<{ content: ContentCardContent }> = ({ content }) => (
  <RenderingProvider rendering={{ componentName: 'ContentCard', content, params: { setElevation: 'true' } }}>
    <ContentCard />
  </RenderingProvider>
);

type TeaserLink = { key: string; link: Link; illustration?: string; teaserText?: string };
type Card = { key: string; content: ContentCardContent };
/** `itemCount` counts every item the division holds, renderable or not, as legacy's `items.length` does. */
type Division = { key: string; title?: string; link?: Link; itemCount: number; links: TeaserLink[]; cards: Card[] };

/**
 * Normalize both lanes' menus into divisions, dropping what cannot be rendered.
 *
 * Resolution happens here rather than inside the leaf components so an item with no destination never
 * reaches the markup at all. Rendering it and returning `null` from the child still emits the `<li>`
 * wrapper, which is what put empty rows under a group heading.
 *
 * A link resolves to nothing when it has no URL, such as an internal link without a page. A division
 * with no items is a plain link, and is dropped when that link resolves to nothing too. One with items
 * stays a dropdown even when none of them can be rendered, as in legacy.
 */
const toDivisions = (items: MegaMenuV2Item[] | undefined): Division[] =>
  (items ?? []).flatMap((item, index): Division[] => {
    const key = item._key ?? item.id ?? String(index);

    if (item._type === 'navItem') {
      const link = linkWithUrl(item.link);
      return link ? [{ key, title: link.text ?? link.href, link, itemCount: 0, links: [], cards: [] }] : [];
    }

    const children = item.items ?? [];
    const links = children.flatMap((child, childIndex): TeaserLink[] => {
      if (item._type !== 'navGroup' && child.templateName !== 'TeaserLink') return [];
      const link = linkWithUrl(child.link);
      if (!link) return [];
      const teaserText = child.teaserText ?? child.description;
      const illustration = child.illustration ?? child.icon;
      return [
        {
          key: child._key ?? child.id ?? String(childIndex),
          link,
          ...(illustration ? { illustration } : {}),
          ...(teaserText ? { teaserText } : {}),
        },
      ];
    });
    const cards = children.flatMap((child, childIndex): Card[] =>
      // Legacy's `isContentCardRendering`: a card item carries an image and a title.
      child.templateName === 'ContentCard' && 'image' in child && 'title' in child
        ? [{ key: child.id ?? String(childIndex), content: child as ContentCardContent }]
        : [],
    );

    const title = item._type === 'navGroup' ? item.heading : item.title;
    const link = linkWithUrl(item.link);
    if (children.length === 0 && !link) return [];

    return [{ key, itemCount: children.length, links, cards, ...(title ? { title } : {}), ...(link ? { link } : {}) }];
  });

export default MegaMenuV2;
